/**
 * HTTP adapter — the real routes of docs/training.md §4.
 *
 * Exposes exactly the methods and return shapes of demoAdapter.js (the spec the UI consumes).
 * Where a backend response differs from what the UI reads, the mapping lives here:
 *   - plain objects (no `({...}, 200)` tuples) and `total_count` → `total` are already
 *     handled by the router; what remains is composition (several routes per UI call),
 *     renaming and filling the defaults the UI assumes (`dossier.facts`, `lock`, …);
 *   - the UI creates programs and scenarios with a client-side id and navigates with it after
 *     saving, so `saveProgram` / `saveScenario` write the server id back on the object passed in.
 */
import { onAuthStateChanged } from "firebase/auth";
import myAxios from "../../../utils/axiosWrapper";
import getConfig from "../../../conf/config";
import { auth } from "../../../conf/firebase";
import { store } from "../../../redux/store";

const baseUrl = () => `${getConfig().API_URL}/audio/api/v1/training`;

/* ── transport ───────────────────────────────────────────────────────── */
const HTTP_MESSAGES = {
  401: "Session expirée, reconnectez-vous.",
  403: "Accès refusé.",
  404: "Élément introuvable.",
  409: "Action impossible dans l'état actuel.",
};

const errorMessage = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail?.errors?.length) return detail.errors.join(" · ");
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(" · ");
  if (status >= 500) return "Le serveur a rencontré une erreur.";
  return HTTP_MESSAGES[status] || (typeof detail === "string" && detail) || err?.message;
};

// Short-lived read caches. Any write clears them, so a refetch after a mutation is always fresh.
const pathCache = new Map();
const sheetCache = new Map();
const PATH_TTL_MS = 4000;
const SHEET_TTL_MS = 10 * 60 * 1000;
// What the live call needs again at completion time (the scenario content and the score to beat).
const sheetByAttempt = new Map();
// Last full scenario fetched for editing, to skip no-op saves (a save on a published one bumps
// its version).
const savedScenarioBody = new Map();

// The auth state is persisted by redux, so on a page refresh the app believes the user is signed in
// before Firebase has restored its session: a request sent now would carry no token (401). Wait for it.
let authReady = null;
const whenSignedIn = () => {
  if (auth.currentUser) return Promise.resolve();
  if (!authReady) {
    authReady = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, () => {
        unsubscribe();
        resolve();
      });
    });
  }
  return authReady;
};

const request = async (method, path, { params, data } = {}) => {
  await whenSignedIn();
  if (method !== "get") {
    pathCache.clear();
    sheetCache.clear();
  }
  try {
    const res = await myAxios.request({ method, url: `${baseUrl()}${path}`, params, data });
    return res.data;
  } catch (err) {
    const e = new Error(errorMessage(err));
    e.status = err?.response?.status;
    throw e;
  }
};
const get = (path, params) => request("get", path, { params });
const post = (path, data = {}) => request("post", path, { data });
const put = (path, data = {}) => request("put", path, { data });
/** A read the screen can live without (e.g. a manager-only enrichment). */
const optional = (promise, fallback) =>
  promise.catch((e) => {
    if (e.status === 403 || e.status === 404) return fallback;
    throw e;
  });

/* ── shared mapping helpers ──────────────────────────────────────────── */
const reduxUser = () => {
  const state = store.getState();
  return state?.user?.user || state?.user || {};
};
const currentOperator = (operatorId) => {
  const u = reduxUser();
  return {
    id: operatorId || u.operator_id,
    first_name: u.first_name || "",
    last_name: u.last_name || "",
    email: u.email || "",
  };
};

const withoutReference = (s) => {
  const { reference_call: ignored, ...safe } = s;
  return safe;
};

/** The UI reads these without guarding, the backend leaves them out when empty. */
const normalizeScenario = (s) =>
  s && {
    ...s,
    persona: { role: "", ...s.persona },
    mission: { statement: "", ...s.mission },
    dossier: { summary: "", facts: [], documents: [], ...s.dossier },
    mission_cues: s.mission_cues || [],
    hints: s.hints || [],
    trainer_advice: s.trainer_advice || "",
    source: { kind: "manual", document_ids: [], call_ids: [], ...s.source },
  };

const highestValidated = (levels = {}) =>
  Object.entries(levels).reduce(
    (max, [key, v]) => (v.status === "validated" ? Math.max(max, Number(key)) : max),
    0
  );

/** Backend enrollment view → the shape the screens read (full `program`, `validated_level`). */
const toEnrollment = (view, program) => ({
  ...view,
  program: program && {
    id: program.id,
    name: program.name,
    brand: program.brand ?? null,
    unit: program.unit,
    levels: program.levels,
  },
  validated_level: highestValidated(view.levels),
});

const levelConfig = ({ state, scenarios, ...cfg }) => cfg;
const programFromPath = (path) => ({ ...path.program, levels: path.levels.map(levelConfig) });

const teamOf = (operator) => ({
  id: operator.team_id,
  name: operator.team_name || "Sans équipe",
});

/* ── events payloads (live call → backend) ───────────────────────────── */
const pick = (obj, keys) =>
  Object.fromEntries(keys.filter((k) => obj[k] != null).map((k) => [k, obj[k]]));

const cueList = (cues) =>
  Array.isArray(cues)
    ? cues
    : Object.entries(cues).map(([cue_id, v]) =>
        typeof v === "string" ? { cue_id, state: v } : { cue_id, ...v }
      );

const eventsBody = (e = {}) => {
  const body = {};
  if (e.transcript) {
    body.transcript = e.transcript
      .filter((t) => t.final !== false && t.text)
      .map((t) => ({
        ...pick(t, ["i", "role", "t", "t_end", "cues"]),
        text: String(t.text).slice(0, 4000),
      }))
      .slice(-200);
  }
  if (e.cues) {
    body.cues = cueList(e.cues).map((c) =>
      pick(c, ["cue_id", "state", "at_s", "confidence", "evidence_turn"])
    );
  }
  if (e.hints_shown) {
    body.hints_shown = e.hints_shown.map((h) => pick(h, ["hint_id", "at_s", "via"]));
  }
  if (e.signals) {
    body.signals = e.signals
      .map((s) => pick(s, ["t", "sentiment", "tension", "score_estimate"]))
      .slice(-200);
  }
  return body;
};

/* ── scenario / program writes ───────────────────────────────────────── */
const SCENARIO_FIELDS = [
  "level",
  "order",
  "title",
  "call_direction",
  "palier_label",
  "is_certification",
  "estimated_duration_s",
  "max_duration_s",
  "persona",
  "mission",
  "dossier",
  "mission_cues",
  "hints",
  "trainer_advice",
];
// `published` is reached through publishScenario, never by a plain save.
const SAVEABLE_STATUS = new Set(["draft", "in_review", "archived"]);

const scenarioBody = (s) => ({
  ...pick(s, SCENARIO_FIELDS),
  ...(s.reference_call ? { reference_call: s.reference_call } : {}),
  ...(SAVEABLE_STATUS.has(s.status) ? { status: s.status } : {}),
});

const programBody = (p) => ({
  ...pick(p, ["name", "description", "brand", "rubric", "levels", "xp_rules"]),
});

/* ── reads reused by several methods ─────────────────────────────────── */
const fetchPath = async (programId) => {
  const hit = pathCache.get(programId);
  if (hit && Date.now() - hit.at < PATH_TTL_MS) return hit.data;
  const data = await get(`/programs/${programId}/path`);
  pathCache.set(programId, { at: Date.now(), data });
  return data;
};

const fetchSheet = async (scenarioId) => {
  const hit = sheetCache.get(scenarioId);
  if (hit && Date.now() - hit.at < SHEET_TTL_MS) return hit.data;
  const data = await get(`/scenarios/${scenarioId}`);
  sheetCache.set(scenarioId, { at: Date.now(), data });
  return data;
};

/** An archived scenario can no longer be opened by a learner: keep its debriefs readable. */
const scenarioOrPlaceholder = (sheet, attempt) =>
  normalizeScenario(
    sheet?.scenario || {
      id: attempt.scenario_id,
      program_id: attempt.program_id,
      level: attempt.level,
      order: 1,
      title: "Scénario archivé",
      persona: { name: "Client" },
    }
  );

const sheetProgram = (sheet, programId) => ({
  id: programId,
  rubric: sheet?.rubric || [],
  levels: sheet?.level ? [sheet.level] : [],
});

const bodyKey = (scenario) => JSON.stringify(scenarioBody(normalizeScenario(scenario)));
const rememberFull = (scenario) => savedScenarioBody.set(scenario.id, bodyKey(scenario));

const TEXT_EXT = /\.(txt|md|markdown|csv|json|log)$/i;
const MAX_INLINE_TEXT = 200000;
const GENERATION_STAGES = [
  "Lecture des documents",
  "Extraction des situations clés",
  "Rédaction des scénarios",
  "Contrôle qualité",
];

export const httpApi = {
  mode: "http",

  /* ── agent ─────────────────────────────────────────────────────────── */
  /** No dedicated route: derived from the signed-in user held in redux. */
  async getContext() {
    const u = reduxUser();
    const algolia = store.getState()?.algolia?.algolia || {};
    return {
      company: {
        id: u.company_id,
        name: algolia.company_name || "",
        display_tz: algolia.display_tz || "Europe/Paris",
      },
      me: { operator_id: u.operator_id, operator: currentOperator(u.operator_id) },
      manager: { id: u.uid, first_name: u.first_name || "", last_name: u.last_name || "" },
      teams: [],
    };
  },

  async getMe() {
    const [me, certs] = await Promise.all([get("/me"), get("/certificates")]);
    const paths = await Promise.all(me.enrollments.map((e) => fetchPath(e.program.id)));
    return {
      operator: me.operator || currentOperator(me.operator_id),
      team: me.team,
      enrollments: me.enrollments.map((e, i) => toEnrollment(e, programFromPath(paths[i]))),
      certificates: certs.certificates,
    };
  },

  async getPath(programId, operatorId) {
    if (operatorId && operatorId !== reduxUser().operator_id) {
      throw new Error("Le parcours d'un autre agent n'est pas disponible.");
    }
    const path = await fetchPath(programId);
    const program = programFromPath(path);
    const levels = path.levels.map((l) => {
      const { state, scenarios, ...cfg } = l;
      return {
        ...cfg,
        ...state,
        scenarios: scenarios.map((s) => {
          let lock = null;
          if (s.state === "locked") lock = state.status === "locked" ? "level" : "palier";
          return { ...s, lock };
        }),
      };
    });
    return { program, enrollment: toEnrollment(path.enrollment, program), levels };
  },

  async getScenario(id) {
    const [sheet, mine] = await Promise.all([
      fetchSheet(id),
      // a manager previewing the sheet has no operator, hence no attempts
      optional(get("/attempts", { scenario_id: id }), { attempts: [] }),
    ]);
    return {
      scenario: normalizeScenario(sheet.scenario),
      program: sheetProgram(sheet, sheet.scenario.program_id),
      attempts: mine.attempts,
      best_score: sheet.best_score ?? null,
      pass_score: sheet.level?.pass_score ?? 70,
    };
  },

  /** `test`: a tester's sandbox call, outside any enrollment (nothing is recorded for a team). */
  async startAttempt(scenarioId, { test = false } = {}) {
    // read before the write below clears the cache: the score to beat, and the scenario content
    const sheet = await fetchSheet(scenarioId).catch(() => null);
    const { attempt, session } = await post("/attempts", {
      scenario_id: scenarioId,
      ...(test ? { test: true } : {}),
    });
    sheetByAttempt.set(attempt.id, sheet);
    // marks the attempt live and stamps started_at; not worth failing the call over
    post(`/attempts/${attempt.id}/events`).catch(() => {});
    return { attempt, session };
  },

  /** Live signals for the transcript so far (Decisions API, or the backend's heuristic). */
  async getSignals(attemptId, { transcript = [], cues = {} } = {}) {
    return post(`/attempts/${attemptId}/signals`, eventsBody({ transcript, cues }));
  },

  async appendEvents(attemptId, events) {
    try {
      await post(`/attempts/${attemptId}/events`, eventsBody(events));
      return { ok: true };
    } catch (e) {
      return { ok: false };
    }
  },

  async completeAttempt(attemptId, payload = {}) {
    if (payload.conversation_id) {
      await post(`/attempts/${attemptId}/events`, {
        session: { conversation_id: payload.conversation_id },
      }).catch(() => {});
    }
    const res = await post(`/attempts/${attemptId}/complete`, {
      ...eventsBody(payload),
      duration_s: payload.duration_s,
    });
    const { attempt } = res;
    const sheet =
      sheetByAttempt.get(attemptId) || (await fetchSheet(attempt.scenario_id).catch(() => null));
    sheetByAttempt.delete(attemptId);
    if (attempt.test) {
      // sandbox call: a debrief only, no xp, level or next step
      return {
        attempt,
        scenario: scenarioOrPlaceholder(sheet, attempt),
        program: { id: attempt.program_id, rubric: sheet?.rubric || [] },
        pass_score: sheet?.level?.pass_score ?? 70,
        previous_best: 0,
        progress: null,
        next_scenario: null,
        certificates: [],
      };
    }
    const path = await fetchPath(attempt.program_id).catch(() => null);

    const pass = sheet?.level?.pass_score ?? 70;
    const previousBest = sheet?.best_score ?? 0;
    const lvl = res.enrollment?.levels?.[String(attempt.level)] || {};
    // the level only just became ready if this call is the one that first passed its scenario
    const justReady =
      lvl.status === "ready_for_validation" && attempt.debrief.score >= pass && previousBest < pass;

    const flat = (path?.levels || []).flatMap((l) => l.scenarios);
    const at = flat.findIndex((s) => s.id === attempt.scenario_id);
    const after = flat[at + 1];
    const current = flat[at] || { id: attempt.scenario_id };

    return {
      attempt,
      scenario: scenarioOrPlaceholder(sheet, attempt),
      program: { id: attempt.program_id, rubric: sheet?.rubric || [] },
      pass_score: pass,
      previous_best: previousBest,
      progress: {
        xp_awarded: res.xp.awarded,
        xp_total: res.xp.total,
        streak: res.enrollment?.stats?.streak_live ?? 0,
        level_before: justReady ? "in_progress" : lvl.status,
        level_after: lvl.status,
        level: attempt.level,
        passed_count: lvl.passed_count ?? 0,
        total: lvl.total ?? 0,
      },
      // the Debrief page hides "next" when it is the scenario itself (e.g. next one still locked)
      next_scenario: after && after.state !== "locked" ? after : current,
      certificates: res.certificates,
    };
  },

  /** Object URL of the call recording, or null when there is none (yet). */
  async getAttemptAudio(attemptId) {
    await whenSignedIn();
    try {
      const res = await myAxios.get(`${baseUrl()}/attempts/${attemptId}/audio`, {
        responseType: "blob",
      });
      return URL.createObjectURL(res.data);
    } catch (err) {
      return null;
    }
  },

  async abandonAttempt(attemptId) {
    try {
      await post(`/attempts/${attemptId}/complete`, { abandon: true });
    } catch (e) {
      /* already graded or gone: nothing to abandon */
    }
    sheetByAttempt.delete(attemptId);
    return { ok: true };
  },

  async getAttempt(id) {
    const { attempt, previous_best: previousBest } = await get(`/attempts/${id}`);
    const sheet = await fetchSheet(attempt.scenario_id).catch(() => null);
    return {
      attempt,
      scenario: scenarioOrPlaceholder(sheet, attempt),
      program: sheetProgram(sheet, attempt.program_id),
      operator: null,
      pass_score: sheet?.level?.pass_score ?? 70,
      previous_best: previousBest ?? 0,
    };
  },

  /** Tester (super admin or training admin): a specimen certificate of a level (nothing is issued). */
  async previewCertificate(programId, level) {
    return (await get(`/programs/${programId}/certificate-preview`, { level })).certificate;
  },

  async getCertificate(id) {
    return (await get(`/certificates/${id}`)).certificate;
  },

  /* ── manager ───────────────────────────────────────────────────────── */
  async getTeam() {
    const [team, progs] = await Promise.all([get("/team"), get("/programs")]);
    const programById = Object.fromEntries(progs.programs.map((p) => [p.id, p]));
    const rows = team.agents
      .filter((a) => a.enrollment)
      .map((a) => ({
        operator: a.operator,
        team: teamOf(a.operator),
        enrollment: toEnrollment(a.enrollment, programById[a.enrollment.program_id] || a.program),
        // only the day is known; the screen renders it as "Hier", "Il y a 3 j", …
        last_attempt_at: a.enrollment.stats?.last_active_date || null,
      }));

    const pending = rows.flatMap((row) =>
      Object.entries(row.enrollment.levels)
        .filter(([, state]) => state.status === "ready_for_validation")
        .map(([level, state]) => ({ row, level: Number(level), state }))
    );
    const teams = [...new Map(rows.map((r) => [r.team.id, r.team])).values()];

    const withRows = new Set(rows.map((r) => r.enrollment.program_id));
    const programs = progs.programs
      .filter((p) => p.status !== "archived")
      .sort((a, b) => Number(withRows.has(b.id)) - Number(withRows.has(a.id)))
      .map((p) => ({
        id: p.id,
        name: p.name,
        brand: p.brand ?? null,
        unit: p.unit,
        levels: p.levels,
      }));
    return { rows, pending, teams, programs };
  },

  async getAgent(operatorId) {
    const [agent, progs] = await Promise.all([get(`/agents/${operatorId}`), get("/programs")]);
    const programById = Object.fromEntries(progs.programs.map((p) => [p.id, p]));
    const programIds = [...new Set(agent.enrollments.map((e) => e.program_id))];
    const lists = await Promise.all(programIds.map((id) => get("/scenarios", { program_id: id })));
    return {
      operator: agent.operator,
      team: teamOf(agent.operator),
      enrollments: agent.enrollments.map((e) => toEnrollment(e, programById[e.program_id])),
      attempts: agent.attempts.filter((a) => a.status === "graded" && a.debrief),
      scenarios: lists
        .flatMap((l) => l.scenarios)
        .map((s) => normalizeScenario(withoutReference(s))),
      comments: agent.comments,
      certificates: agent.certificates,
    };
  },

  async addComment({ operator_id, attempt_id = null, text }) {
    const body = { operator_id, text, ...(attempt_id ? { attempt_id } : {}) };
    return (await post("/comments", body)).comment;
  },

  async validateLevel(enrollmentId, level, { reject = false, comment = "" } = {}) {
    const res = await post(`/enrollments/${enrollmentId}/levels/${level}/validate`, {
      reject,
      comment: reject && comment ? comment : undefined,
    });
    if (reject) return { ok: true, rejected: true, enrollment: res.enrollment };
    if (comment) {
      // the backend only records the comment of a rejection; keep the validation one too
      await post("/comments", { operator_id: res.enrollment.operator_id, text: comment }).catch(
        () => {}
      );
    }
    return { ok: true, certificate: res.certificate, enrollment: res.enrollment };
  },

  /* ── programs & scenarios (management) ─────────────────────────────── */
  async listPrograms() {
    const [progs, team] = await Promise.all([
      get("/programs"),
      optional(get("/team"), { agents: [] }),
    ]);
    const enrolled = {};
    team.agents.forEach((a) => {
      if (a.enrollment)
        enrolled[a.enrollment.program_id] = (enrolled[a.enrollment.program_id] || 0) + 1;
    });
    return progs.programs.map((p) => {
      const c = p.scenario_counts || {};
      return {
        ...p,
        scenario_counts: {
          total: Object.values(c).reduce((sum, n) => sum + n, 0),
          published: c.published || 0,
          draft: c.draft || 0,
          in_review: c.in_review || 0,
        },
        enrolled: enrolled[p.id] || 0,
      };
    });
  },

  async getProgram(id) {
    const [prog, scen, team] = await Promise.all([
      get(`/programs/${id}`),
      get("/scenarios", { program_id: id }),
      optional(get("/team", { program_id: id }), { agents: [] }),
    ]);
    const scenarios = scen.scenarios.map(normalizeScenario);
    scenarios.forEach(rememberFull);
    return {
      program: prog.program,
      scenarios,
      enrollments: team.agents
        .filter((a) => a.enrollment)
        .map((a) => ({ ...toEnrollment(a.enrollment, prog.program), operator: a.operator })),
    };
  },

  async saveProgram(program) {
    const body = programBody(program);
    try {
      const patch = program.status === "archived" ? { ...body, status: "archived" } : body;
      return (await put(`/programs/${program.id}`, patch)).program;
    } catch (e) {
      if (e.status !== 404) throw e;
    }
    // unknown id: the UI created it locally (blankProgram) — create it, and hand the id back
    const { program: created } = await post("/programs", { ...body, source: program.source });
    // eslint-disable-next-line no-param-reassign
    program.id = created.id;
    return created;
  },

  async publishProgram(id) {
    // "publishing the program" in the UI also publishes the scenarios awaiting review
    const review = await get("/scenarios", { program_id: id, status: "in_review" });
    await Promise.all(review.scenarios.map((s) => post(`/scenarios/${s.id}/publish`)));
    return (await post(`/programs/${id}/publish`)).program;
  },

  async getScenarioFull(id) {
    const { scenario } = await get(`/scenarios/${id}/full`);
    const { program } = await get(`/programs/${scenario.program_id}`);
    rememberFull(scenario);
    return { scenario: normalizeScenario(scenario), program };
  },

  async saveScenario(scenario) {
    const body = scenarioBody(scenario);
    if (savedScenarioBody.get(scenario.id) === bodyKey(scenario)) return scenario; // no change
    try {
      const { scenario: saved } = await put(`/scenarios/${scenario.id}`, body);
      rememberFull(saved);
      return normalizeScenario(saved);
    } catch (e) {
      if (e.status !== 404) throw e;
    }
    // unknown id: created locally (blankScenario) — create it, and hand the id back
    const { status, ...fields } = body;
    const { scenario: created } = await post("/scenarios", {
      ...fields,
      program_id: scenario.program_id,
      key: scenario.key,
      source: scenario.source,
    });
    let saved = created;
    if (status && status !== "draft")
      saved = (await put(`/scenarios/${created.id}`, { status })).scenario;
    // eslint-disable-next-line no-param-reassign
    scenario.id = saved.id;
    rememberFull(saved);
    return normalizeScenario(saved);
  },

  async publishScenario(id) {
    const { scenario } = await post(`/scenarios/${id}/publish`);
    rememberFull(scenario);
    return normalizeScenario(scenario);
  },

  async listOperators() {
    const { programs } = await get("/programs");
    // in-scope operators not enrolled in a program only appear when asking for that program
    const [enrolled, others] = await Promise.all([
      get("/team"),
      programs.length ? get("/team", { program_id: programs[0].id }) : { agents: [] },
    ]);
    const operators = new Map();
    [...enrolled.agents, ...others.agents].forEach((a) => operators.set(a.operator.id, a.operator));
    const list = [...operators.values()];
    return {
      operators: list,
      teams: [...new Map(list.map((o) => [o.team_id, teamOf(o)])).values()],
      enrollments: enrolled.agents
        .filter((a) => a.enrollment)
        .map((a) => ({
          id: a.enrollment.id,
          operator_id: a.operator.id,
          program_id: a.enrollment.program_id,
        })),
    };
  },

  async assign({ program_id, operator_ids, target }) {
    const res = await post("/assignments", { program_id, operator_ids, target });
    return {
      ok: true,
      assignment_id: res.assignment_id,
      count: res.created.length + res.updated.length,
      skipped: res.skipped,
    };
  },

  /* ── AI generation ─────────────────────────────────────────────────── */
  /** Plain-text files travel inline; the others are uploaded straight to storage (signed URL). */
  async uploadDocument(file) {
    const mime = file.type || "application/octet-stream";
    const inline = mime.startsWith("text/") || TEXT_EXT.test(file.name);
    const text = inline ? (await file.text()).slice(0, MAX_INLINE_TEXT) : undefined;
    const { document: doc, upload } = await post("/documents", {
      name: file.name,
      mime,
      size: file.size,
      text,
    });
    let done = doc;
    if (!inline) {
      if (!upload?.url)
        throw new Error("Le dépôt de fichiers n'est pas disponible pour le moment.");
      // a bare fetch: the signed URL must not carry our Authorization header
      const res = await fetch(upload.url, {
        method: upload.method || "PUT",
        headers: upload.headers,
        body: file,
      });
      if (!res.ok) throw new Error("Le dépôt du fichier a échoué.");
      done = (await post(`/documents/${doc.id}/uploaded`)).document;
    }
    return {
      id: done.id,
      name: done.name,
      size: done.size,
      mime: done.mime,
      text_status: done.text_status,
      uploaded_at: done.created_at,
    };
  },

  async startGeneration(brief) {
    const { document_ids: documentIds = [], goal, audience, ...rest } = brief;
    const { job } = await post("/generation", {
      document_ids: documentIds,
      brief: { ...rest, description: goal || "", goal, audience },
    });
    return { id: job.id };
  },

  async getGeneration(id) {
    const { job } = await get(`/generation/${id}`);
    const progress = Math.min(1, (job.progress || 0) / 100);
    if ((job.status === "pending" || job.status === "applied") && job.proposal) {
      return {
        id,
        status: "pending",
        progress: 1,
        stages: GENERATION_STAGES,
        stage_index: GENERATION_STAGES.length,
        proposal: {
          program: job.proposal.program,
          scenarios: job.proposal.scenarios.map(normalizeScenario),
        },
      };
    }
    if (job.status === "failed" || job.status === "cancelled" || job.status === "rejected") {
      return { id, status: "failed", progress, error: job.error || "La génération a échoué." };
    }
    const stageIdx = Math.min(
      GENERATION_STAGES.length - 1,
      Math.floor(progress * GENERATION_STAGES.length)
    );
    return {
      id,
      status: "running",
      progress,
      stage: GENERATION_STAGES[stageIdx],
      stages: GENERATION_STAGES,
      stage_index: stageIdx,
    };
  },

  async applyGeneration(id, { program, scenarios }) {
    const res = await post(`/generation/${id}/apply`, { draft: { program, scenarios } });
    return { program_id: res.program.id };
  },
};
